import React, { useRef } from 'react'
import styled from '@emotion/styled'
import { motion } from 'motion/react'
import plural from '@mindfulchefuk/utils/plural'
import { Text, theme, Icon, ColorToken } from '@mindfulchefuk/design-system'
import { RecipeFeedback } from '@mindfulchefuk/features/Recipes/interfaces'
import { trackAnalyticsEvent } from '@mindfulchefuk/utils/analytics/amplitude/amplitudeAdapter'

type TRecipeRating = {
  feedback: RecipeFeedback
  productId: string
  productName: string
  starColor?: ColorToken
}

const RatingsWrapper = styled(motion.span)`
  cursor: pointer;
  position: relative;
`

const Popup = styled(motion.div)`
  width: 240px;
  position: absolute;
  box-shadow: ${theme.shadows.standard};
  padding: ${theme.space[6]} ${theme.space[12]};
  border-radius: ${theme.space[12]};
  background-color: ${theme.colors.white};
  > div {
    text-wrap: balance;
    overflow-wrap: break-word;
  }
`

const UnderlinedText = styled('span')`
  text-decoration: underline;
`

const popupInitial = {
  bottom: 24,
  opacity: 0,
  left: -60,
  scale: 0.7,
  display: 'none',
}

const popupVariants = {
  active: {
    bottom: 25,
    left: -60,
    opacity: 1,
    scale: 1,
    display: 'block',
  },
}

export const recipeRoundelRatingCopy = (rating: number) => {
  const tasty = rating >= 4 ? ' a tasty' : ''
  const stars = plural('star', 'stars')(rating)

  return `You rated this recipe${tasty} ${rating} ${stars}`
}

export const RecipeRating = ({
  feedback,
  productId,
  productName,
  starColor,
}: TRecipeRating) => {
  const ref = useRef(null)

  const iconColor = starColor ?? theme.colors['pepper-500']

  const { description, rating } = feedback

  const feedbackDescription = feedback?.description || ''

  const truncatedFeedback =
    feedbackDescription.length > 500
      ? feedbackDescription.slice(0, 499)
      : feedbackDescription

  const feedbackText = feedbackDescription
    ? `${recipeRoundelRatingCopy(rating)}${
        description && `, with the comment "${truncatedFeedback}"`
      }`
    : recipeRoundelRatingCopy(rating)

  const handleTouchEnd = () => {
    const isActive = document.activeElement === ref.current
    if (isActive) {
      ref.current.blur()
    } else {
      ref.current.focus()
      trackAnalyticsEvent({
        name: 'recipe card badge viewed',
        data: {
          'display type': 'rating',
          'hover info type': 'info text',
          'product name': productName,
          'product id': productId,
        },
      })
    }

    return false
  }

  return (
    <RatingsWrapper
      whileHover="active"
      onClick={(e: React.MouseEvent<HTMLSpanElement, MouseEvent>) =>
        e.stopPropagation()
      }
      onMouseDown={(e: React.MouseEvent<HTMLSpanElement, MouseEvent>) =>
        e.preventDefault()
      }
      onTouchEnd={handleTouchEnd}
      onHoverStart={() =>
        trackAnalyticsEvent({
          name: 'recipe card badge viewed',
          data: {
            'display type': 'rating',
            'hover info type': 'info text',
            'product name': productName,
            'product id': productId,
          },
        })
      }
    >
      {feedbackText.length > 0 && (
        <Popup initial={popupInitial} variants={popupVariants}>
          <Text variant="body-xs-500" maxWidth={240} color="black">
            {feedbackText}
          </Text>
        </Popup>
      )}
      <Text
        color="pepper-500"
        variant="body-md"
        display="flex"
        alignItems="center"
      >
        <UnderlinedText>{rating}</UnderlinedText>
        <Icon type="star-filled" size={12} marginLeft={4} color={iconColor} />
      </Text>
    </RatingsWrapper>
  )
}
